import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { PageMeta } from '../components/seo/PageMeta'
import { api } from '../lib/api'
import { CHIRO_CHECKLIST_TOKEN_STORAGE_KEY } from '../lib/chiropracticChecklistToken'
import { chiropracticSchema } from '../lib/schema'

type CaptchaProvider = 'turnstile' | 'recaptcha' | null

const CANONICAL = 'https://nexttechconsultants.com/chiropractic'
const PAGE_TITLE = 'HIPAA-Compliant IT for Chiropractic Practices'
const PAGE_DESCRIPTION =
  'Managed IT built for chiropractic offices in Oregon and Washington. HIPAA compliance, EHR support, and cybersecurity — with a $30,000 SPECTRA Care coverage. Free IT assessment for your practice.'

const problemCards: readonly {
  title: string
  body: string
  /** When set, body is the collapsed teaser; this is revealed after “Learn more”. */
  bodyExpanded?: string
}[] = [
  {
    title: 'HIPAA Compliance',
    body:
      'Patient records, billing data, and clinical notes have strict protection requirements. A misconfigured system or an untrained staff member is all it takes for a reportable breach — and HIPAA penalties can reach $50,000 per violation, with annual caps up to $1.9 million.',
  },
  {
    title: 'EHR Reliability',
    body:
      'When ChiroTouch goes down or Jane freezes mid-appointment, your front desk stops and your schedule backs up. Your IT provider should understand how your practice management software works — not just that it exists.',
  },
  {
    title: 'Cyber Insurance',
    body:
      "Most cyber insurance applications require detailed documentation of your security posture. Without the right controls in place — and verified proof — you either can't qualify or you're overpaying. As a SPECTRA Certified Partner,",
    bodyExpanded:
      'NextTech Consultants can connect your practice with SPECTRA-approved brokers for preferential cyber insurance coverage. No lengthy questionnaires — your SPECTRA certification does the work. Quotes typically within 2–3 business days.',
  },
]

const coverageItems: { label: string; description: string }[] = [
  {
    label: 'HIPAA-compliant infrastructure',
    description:
      'Role-based access controls, audit logs, encrypted backups',
  },
  {
    label: 'EHR and practice management support',
    description: 'ChiroTouch, Jane, and other platforms your team relies on',
  },
  {
    label: '24/7 monitoring and endpoint protection',
    description: 'We catch problems before your front desk does',
  },
  {
    label: 'Staff devices',
    description: 'Laptops, desktops, tablets — managed and secured',
  },
  {
    label: 'Backup and disaster recovery',
    description: 'Certified by SPECTRA, warranted against failure',
  },
  {
    label: 'Cyber insurance qualification',
    description:
      'SPECTRA certification replaces lengthy questionnaires, quotes typically within 2–3 business days',
  },
]

function SpectraBadgeRow() {
  const badges = [
    { webp: '/badges/spectra-partner.webp', png: '/badges/spectra-partner.png', alt: 'SPECTRA Certified Partner' },
    { webp: '/badges/spectra-endpoint.webp', png: '/badges/spectra-endpoint.png', alt: 'SPECTRA Endpoint Security' },
    { webp: '/badges/spectra-bec.webp', png: '/badges/spectra-bec.png', alt: 'SPECTRA BEC Protection' },
    { webp: '/badges/spectra-backup.webp', png: '/badges/spectra-backup.png', alt: 'SPECTRA Backup' },
  ] as const
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {badges.map((b) => (
        <picture key={b.alt}>
          <source srcSet={b.webp} type="image/webp" />
          <img src={b.png} alt={b.alt} width={120} height={40} style={{ height: 40, width: 'auto', display: 'block' }} />
        </picture>
      ))}
    </div>
  )
}

export default function Chiropractic() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [captchaProvider, setCaptchaProvider] = useState<CaptchaProvider>(null)
  const [captchaSiteKey, setCaptchaSiteKey] = useState('')
  const [cyberInsuranceMoreOpen, setCyberInsuranceMoreOpen] = useState(false)
  const turnstileWidgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    api
      .get<{ captcha_provider: CaptchaProvider; captcha_site_key: string }>('/api/contact/config')
      .then(({ data }) => {
        setCaptchaProvider(data.captcha_provider)
        setCaptchaSiteKey(data.captcha_site_key || '')
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (captchaProvider !== 'turnstile' || !captchaSiteKey) return
    if (document.querySelector('script[src*="turnstile"]')) return
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      const container = document.getElementById('chiro-turnstile-widget')
      if (
        container &&
        typeof (window as unknown as { turnstile?: { render: (el: string | HTMLElement, o: { sitekey: string }) => string } })
          .turnstile?.render === 'function'
      ) {
        const turnstile = (window as unknown as { turnstile: { render: (el: string | HTMLElement, o: { sitekey: string }) => string } })
          .turnstile
        turnstileWidgetIdRef.current = turnstile.render(container, { sitekey: captchaSiteKey })
      }
    }
    document.head.appendChild(script)
    return () => {
      if (
        turnstileWidgetIdRef.current !== null &&
        typeof (window as unknown as { turnstile?: { remove: (id: string) => void } }).turnstile?.remove === 'function'
      ) {
        const w = window as unknown as { turnstile: { remove: (id: string) => void } }
        w.turnstile.remove(turnstileWidgetIdRef.current)
      }
    }
  }, [captchaProvider, captchaSiteKey])

  useEffect(() => {
    if (captchaProvider !== 'recaptcha' || !captchaSiteKey) return
    if (document.querySelector('script[src*="recaptcha"]')) return
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${captchaSiteKey}`
    script.async = true
    document.head.appendChild(script)
  }, [captchaProvider, captchaSiteKey])

  const handleChecklistSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const firstName = (data.get('first_name') as string) || ''
    const lastName = (data.get('last_name') as string) || ''
    const email = (data.get('email') as string) || ''
    const company = (data.get('company') as string) || ''
    const website = (data.get('website') as string) || ''
    const name = `${firstName} ${lastName}`.trim()

    let turnstileToken: string | undefined
    let recaptchaToken: string | undefined

    if (captchaProvider === 'turnstile' && turnstileWidgetIdRef.current !== null) {
      const turnstile = (window as unknown as { turnstile?: { getResponse: (id: string) => string } }).turnstile
      turnstileToken = turnstile?.getResponse(turnstileWidgetIdRef.current) || undefined
    }
    if (captchaProvider === 'recaptcha' && captchaSiteKey) {
      const g = (window as unknown as {
        grecaptcha?: { ready: (cb: () => void) => void; execute: (key: string, o: { action: string }) => Promise<string> }
      }).grecaptcha
      if (g) {
        recaptchaToken = await new Promise<string | undefined>((resolve) => {
          g.ready!(() => {
            g.execute!(captchaSiteKey, { action: 'chiro_checklist_submit' }).then(resolve).catch(() => resolve(undefined))
          })
        })
      }
    }

    setLoading(true)
    setError(false)
    try {
      const res = await api.post('/api/contact/submit', {
        name,
        email,
        company: company || undefined,
        message: 'Chiropractic checklist download request',
        website: website || undefined,
        turnstile_token: turnstileToken,
        recaptcha_token: recaptchaToken,
      })
      const token = res.data?.checklist_download_token
      if (typeof token === 'string' && token.length > 0) {
        sessionStorage.setItem(CHIRO_CHECKLIST_TOKEN_STORAGE_KEY, token)
      }
      navigate('/chiropractic/thank-you')
    } catch {
      setError(true)
      if (
        captchaProvider === 'turnstile' &&
        turnstileWidgetIdRef.current !== null &&
        typeof (window as unknown as { turnstile?: { reset: (id: string) => void } }).turnstile?.reset === 'function'
      ) {
        const w = window as unknown as { turnstile: { reset: (id: string) => void } }
        w.turnstile.reset(turnstileWidgetIdRef.current)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageMeta title={PAGE_TITLE} description={PAGE_DESCRIPTION} canonical={CANONICAL} schema={chiropracticSchema} />

      <div className="font-body text-[var(--white)]">
        {/* Hero */}
        <section style={{ background: 'var(--bg)', padding: '120px 0 100px' }}>
          <div className="max-w-6xl mx-auto px-6">
            <p
              style={{
                textTransform: 'uppercase',
                color: 'var(--accent)',
                letterSpacing: '0.12em',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              Managed IT for chiropractic practices
            </p>
            <h1
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(40px, 6vw, 80px)',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              <span style={{ color: 'var(--accent)' }}>Your EHR</span> shouldn&apos;t go down on a Monday morning.
              <br />
              <span className="block mt-2">And your patient data shouldn&apos;t be one weak password away from a breach.</span>
            </h1>
            <p style={{ fontSize: 18, color: 'var(--white-dim)', maxWidth: 600, lineHeight: 1.7, marginBottom: 36 }}>
              NextTech Consultants provides HIPAA-compliant managed IT for multi-provider chiropractic offices across Oregon and
              Washington. We keep your systems running, your patient data protected, and your practice audit-ready — so you can focus
              on your patients.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <a href="/contact#booking" className="btn-primary inline-flex items-center justify-center text-center">
                Book a Free IT Assessment Call
              </a>
              <a href="#checklist-form" className="btn-ghost inline-flex items-center justify-center text-center">
                Download the Free Security Checklist
              </a>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section style={{ background: 'var(--bg-2)', padding: '32px 0' }}>
          <p
            className="text-center px-6"
            style={{ color: 'var(--white-dim)', fontSize: 15, lineHeight: 1.6 }}
          >
            11 Years in Business · 100+ Pacific Northwest Clients · HIPAA-Compliant IT · SPECTRA Certified · 97% Client Retention
          </p>
        </section>

        {/* Problem */}
        <section style={{ background: 'var(--bg)', padding: '100px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <p
              style={{
                textTransform: 'uppercase',
                color: 'var(--accent)',
                letterSpacing: '0.12em',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              What keeps chiropractic offices exposed
            </p>
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(32px, 4vw, 52px)',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 48,
                maxWidth: 900,
              }}
            >
              Most IT providers weren&apos;t built for healthcare.
              <br />
              Yours shouldn&apos;t be learning on the job.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {problemCards.map((card) => {
                const expandable = Boolean(card.bodyExpanded?.length)
                const moreOpen = expandable && cyberInsuranceMoreOpen
                const linkBtnStyle: CSSProperties = {
                  color: 'var(--accent)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  fontFamily: 'inherit',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }
                return (
                  <div
                    key={card.title}
                    style={{
                      background: 'var(--bg-2)',
                      border: '1px solid rgba(78,125,233,0.15)',
                      borderRadius: 8,
                      padding: 32,
                    }}
                  >
                    <h3
                      style={{
                        fontFamily: '"Barlow Condensed", sans-serif',
                        fontWeight: 800,
                        fontSize: 20,
                        textTransform: 'uppercase',
                        color: 'var(--accent)',
                        marginBottom: 14,
                      }}
                    >
                      {card.title}
                    </h3>
                    <p style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--white-dim)' }}>
                      {card.body}
                      {expandable && !moreOpen && (
                        <>
                          {' '}
                          <button
                            type="button"
                            onClick={() => setCyberInsuranceMoreOpen(true)}
                            style={linkBtnStyle}
                            aria-expanded={false}
                          >
                            Learn more
                          </button>
                        </>
                      )}
                      {expandable && moreOpen && (
                        <>
                          {' '}
                          {card.bodyExpanded}{' '}
                          <button
                            type="button"
                            onClick={() => setCyberInsuranceMoreOpen(false)}
                            style={linkBtnStyle}
                            aria-expanded={true}
                          >
                            Show less
                          </button>
                        </>
                      )}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* What we cover */}
        <section style={{ background: 'var(--bg-2)', padding: '100px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <p
              style={{
                textTransform: 'uppercase',
                color: 'var(--accent)',
                letterSpacing: '0.12em',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              Built for your practice
            </p>
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(32px, 4vw, 52px)',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 48,
              }}
            >
              Everything your practice depends on. Monitored, managed, and protected.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              {coverageItems.map((item) => (
                <div key={item.label} className="flex gap-4">
                  <Check className="shrink-0 mt-1" style={{ color: 'var(--accent)', width: 22, height: 22 }} aria-hidden />
                  <div>
                    <p className="font-semibold text-[var(--white)] mb-1">{item.label}</p>
                    <p style={{ color: 'var(--white-dim)', fontSize: 15, lineHeight: 1.7 }}>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPECTRA */}
        <section
          style={{
            background: 'var(--bg-3)',
            padding: '80px 0',
            borderTop: '1px solid var(--accent-line)',
            borderBottom: '1px solid var(--accent-line)',
          }}
        >
          <div className="max-w-6xl mx-auto px-6 text-center">
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(28px, 3.5vw, 44px)',
                lineHeight: 1.15,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              Your practice doesn&apos;t just get protected. It gets certified.
            </h2>
            <p
              className="mx-auto"
              style={{ color: 'var(--white-dim)', fontSize: 17, lineHeight: 1.75, maxWidth: 640, marginBottom: 40 }}
            >
              Chiropractic offices with 12 or more staff receive three SPECTRA Certificates of Resilience — covering Endpoint
              Security, BEC Protection, and Backup. Each is independently verified by SPECTRA, an independent cybersecurity organization.
              Each carries $10,000 in SPECTRA Care coverage. If a covered service fails, SPECTRA refunds your practice directly.
              That&apos;s $30,000 in combined coverage, included in your plan.
            </p>
            <SpectraBadgeRow />
          </div>
        </section>

        {/* Social proof */}
        <section style={{ background: 'var(--bg)', padding: '100px 0' }}>
          <div className="max-w-6xl mx-auto px-6">
            <p
              style={{
                textTransform: 'uppercase',
                color: 'var(--accent)',
                letterSpacing: '0.12em',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              Trusted by Pacific Northwest practices
            </p>
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(32px, 4vw, 48px)',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 40,
                textAlign: 'center',
              }}
            >
              We work with chiropractic offices that can&apos;t afford downtime.
            </h2>
            <blockquote
              className="mx-auto"
              style={{
                maxWidth: 720,
                borderLeft: '4px solid var(--accent-line)',
                paddingLeft: 24,
                fontStyle: 'italic',
                color: 'var(--white-dim)',
                fontSize: 17,
                lineHeight: 1.75,
                marginBottom: 48,
              }}
            >
              A multi-provider chiropractic practice in Oregon came to us after their previous IT provider couldn&apos;t support their
              EHR or provide documentation for their cyber insurance application. Within 60 days they were fully onboarded,
              HIPAA-compliant, and SPECTRA certified — with $30,000 in SPECTRA Care coverage included in their plan. Their office
              manager told us: &apos;I don&apos;t have to worry about IT anymore. It just works.&apos;
            </blockquote>
            <p className="text-center" style={{ color: 'var(--white-dim)', fontSize: 15, lineHeight: 1.65 }}>
              11 years in business · 97% client retention · 100+ Pacific Northwest businesses · $30,000 SPECTRA Care coverage for
              qualifying clients
            </p>
          </div>
        </section>

        {/* Checklist form */}
        <section id="checklist-form" style={{ background: 'var(--bg-2)', padding: '100px 0' }}>
          <div className="max-w-xl mx-auto px-6">
            <p
              style={{
                textTransform: 'uppercase',
                color: 'var(--accent)',
                letterSpacing: '0.12em',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              Free resource for your practice
            </p>
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(28px, 3.5vw, 40px)',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 16,
                textAlign: 'center',
              }}
            >
              Is your chiropractic practice as secure as you think?
            </h2>
            <p style={{ color: 'var(--white-dim)', fontSize: 16, lineHeight: 1.7, marginBottom: 32, textAlign: 'center' }}>
              Download our Business Security Checklist — 10 specific steps healthcare practices can take right now to reduce their risk.
              Free, no fluff, written for practices like yours.
            </p>

            {error && (
              <div
                className="mb-6 p-4 rounded-lg"
                style={{ backgroundColor: 'var(--bg-3)', borderLeft: '4px solid #ef4444' }}
              >
                <p style={{ color: 'var(--white-dim)' }}>
                  Something went wrong. Please call us at 971.238.4801 or try again.
                </p>
              </div>
            )}

            <form onSubmit={handleChecklistSubmit} className="space-y-4">
              <div
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
              >
                <label htmlFor="chiro-website">Website</label>
                <input id="chiro-website" type="text" name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="chiro-first" className="block text-sm mb-2" style={{ color: 'var(--white-dim)' }}>
                    First Name *
                  </label>
                  <input
                    id="chiro-first"
                    name="first_name"
                    required
                    autoComplete="given-name"
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
                <div>
                  <label htmlFor="chiro-last" className="block text-sm mb-2" style={{ color: 'var(--white-dim)' }}>
                    Last Name *
                  </label>
                  <input
                    id="chiro-last"
                    name="last_name"
                    required
                    autoComplete="family-name"
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="chiro-email" className="block text-sm mb-2" style={{ color: 'var(--white-dim)' }}>
                  Practice Email *
                </label>
                <input
                  id="chiro-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-lg font-body"
                  style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                />
              </div>
              <div>
                <label htmlFor="chiro-company" className="block text-sm mb-2" style={{ color: 'var(--white-dim)' }}>
                  Practice Name *
                </label>
                <input
                  id="chiro-company"
                  name="company"
                  required
                  autoComplete="organization"
                  className="w-full px-4 py-3 rounded-lg font-body"
                  style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                />
              </div>
              {captchaProvider === 'turnstile' && (
                <div className="flex justify-center">
                  <div id="chiro-turnstile-widget" />
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Me the Checklist'}
              </button>
              <p className="text-center" style={{ color: 'var(--white-dim)', fontSize: 13 }}>
                We&apos;ll also send you practical IT tips for chiropractic practices. Unsubscribe any time.
              </p>
            </form>
          </div>
        </section>

        {/* Final CTA */}
        <section style={{ background: 'var(--navy)', padding: '120px 0', textAlign: 'center' }}>
          <div className="max-w-2xl mx-auto px-6">
            <h2
              style={{
                fontFamily: '"Barlow Condensed", sans-serif',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontSize: 'clamp(28px, 3.5vw, 44px)',
                lineHeight: 1.15,
                color: 'var(--white)',
                marginBottom: 20,
              }}
            >
              Ready to stop worrying about IT and get back to your patients?
            </h2>
            <p className="mx-auto" style={{ color: 'var(--white-dim)', maxWidth: 560, lineHeight: 1.75, marginBottom: 32 }}>
              Book a free 30-minute IT assessment. We&apos;ll review your current setup, identify your biggest risks, and tell you
              exactly what we&apos;d prioritize for a practice your size. No pitch. Just a useful conversation.
            </p>
            <a href="/contact#booking" className="btn-primary inline-flex items-center justify-center">
              Book Your Free IT Assessment Call
            </a>
            <p className="mt-6" style={{ color: 'var(--white-dim)', fontSize: 14 }}>
              971.238.4801 | info@nexttechconsultants.com
            </p>
          </div>
        </section>
      </div>
    </>
  )
}

export { Chiropractic as Component }
